<template>
    <BaseModal
        :title="isEditing ? 'Edit Teacher' : 'Add Teacher'"
        :loading="loading"
        :submit-text="isEditing ? 'Update' : 'Create'"
        max-width="4xl"
        @close="$emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Personal Information Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Personal Information
                </h4>
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Name *
                        </label>
                        <input
                            v-model="form.name"
                            type="text"
                            required
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.name,
                            }"
                        />
                        <p
                            v-if="errors.name && errors.name.length > 0"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.name[0] }}
                        </p>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Email *
                        </label>
                        <input
                            v-model="form.email"
                            type="email"
                            required
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.email,
                            }"
                        />
                        <p
                            v-if="errors.email && errors.email.length > 0"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.email[0] }}
                        </p>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Birth Date
                        </label>
                        <input
                            v-model="form.birth_date"
                            type="date"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.birth_date,
                            }"
                        />
                        <p
                            v-if="
                                errors.birth_date &&
                                errors.birth_date.length > 0
                            "
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.birth_date[0] }}
                        </p>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Gender
                        </label>
                        <div class="flex gap-6">
                            <div class="flex items-center">
                                <input
                                    v-model="form.gender"
                                    type="radio"
                                    id="male"
                                    value="male"
                                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <label
                                    for="male"
                                    class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                                >
                                    Male
                                </label>
                            </div>
                            <div class="flex items-center">
                                <input
                                    v-model="form.gender"
                                    type="radio"
                                    id="female"
                                    value="female"
                                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <label
                                    for="female"
                                    class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                                >
                                    Female
                                </label>
                            </div>
                        </div>
                        <p
                            v-if="errors.gender && errors.gender.length > 0"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.gender[0] }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Professional Information Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Professional Information
                </h4>
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            NIP *
                        </label>
                        <input
                            v-model="form.nip"
                            type="text"
                            required
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.nip,
                            }"
                        />
                        <p
                            v-if="errors.nip && errors.nip.length > 0"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.nip[0] }}
                        </p>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Specialization
                        </label>
                        <input
                            v-model="form.specialization"
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.specialization,
                            }"
                        />
                        <p
                            v-if="
                                errors.specialization &&
                                errors.specialization.length > 0
                            "
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.specialization[0] }}
                        </p>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Qualification
                        </label>
                        <input
                            v-model="form.qualification"
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.qualification,
                            }"
                        />
                        <p
                            v-if="
                                errors.qualification &&
                                errors.qualification.length > 0
                            "
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.qualification[0] }}
                        </p>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Hire Date
                        </label>
                        <input
                            v-model="form.hire_date"
                            type="date"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.hire_date,
                            }"
                        />
                        <p
                            v-if="
                                errors.hire_date && errors.hire_date.length > 0
                            "
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.hire_date[0] }}
                        </p>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Phone
                        </label>
                        <input
                            v-model="form.phone"
                            type="tel"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.phone,
                            }"
                        />
                        <p
                            v-if="errors.phone && errors.phone.length > 0"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.phone[0] }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Contact Information Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Contact Information
                </h4>
                <div>
                    <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                        Address
                    </label>
                    <textarea
                        v-model="form.address"
                        rows="3"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        :class="{
                            'border-red-500': errors.address,
                        }"
                    ></textarea>
                    <p
                        v-if="errors.address && errors.address.length > 0"
                        class="mt-1 text-sm text-red-600"
                    >
                        {{ errors.address[0] }}
                    </p>
                </div>
            </div>

            <!-- Settings Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Settings
                </h4>
                <div class="flex items-center">
                    <input
                        v-model="form.is_homeroom"
                        type="checkbox"
                        id="is_homeroom"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                        for="is_homeroom"
                        class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                    >
                        Homeroom Teacher
                    </label>
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import type { Teacher } from "@/api/teacher";
import { useTeacherStore } from "@/stores/teacherStore";
import BaseModal from "@/components/BaseModal.vue";

interface Props {
    teacher?: Teacher | null;
    isEditing: boolean;
}

interface Emits {
    close: [];
    save: [teacherData: any];
    saved: [];
}

const props = withDefaults(defineProps<Props>(), {
    teacher: null,
});

const emit = defineEmits<Emits>();

const teacherStore = useTeacherStore();

const isEditing = computed(() => props.isEditing);

const loading = ref(false);
const errors = ref<Record<string, string[]>>({});

const form = ref({
    name: "",
    email: "",
    nip: "",
    specialization: "",
    phone: "",
    address: "",
    birth_date: "",
    qualification: "",
    hire_date: "",
    gender: "",
    is_homeroom: false,
});

const resetForm = () => {
    form.value = {
        name: "",
        email: "",
        nip: "",
        specialization: "",
        phone: "",
        address: "",
        birth_date: "",
        qualification: "",
        hire_date: "",
        gender: "",
        is_homeroom: false,
    };
    resetErrors();
};

const resetErrors = () => {
    errors.value = {};
};

watch(
    () => props.teacher,
    (newTeacher: Teacher | null) => {
        resetErrors();
        if (newTeacher) {
            form.value = {
                name: newTeacher.user?.name || "",
                email: newTeacher.user?.email || "",
                nip: newTeacher.nip || "",
                specialization: newTeacher.specialization || "",
                phone: newTeacher.phone || "",
                address: newTeacher.address || "",
                birth_date: newTeacher.birth_date?.split("T")[0] ?? "",
                qualification: newTeacher.qualification || "",
                hire_date: newTeacher.hire_date?.split("T")[0] ?? "",
                gender: newTeacher.gender || "",
                is_homeroom: newTeacher.is_homeroom || false,
            };
        } else {
            resetForm();
        }
    },
    { immediate: true },
);

watch(
    () => props.teacher,
    () => {
        resetErrors();
    },
);

const validateForm = () => {
    const newErrors: Record<string, string[]> = {};

    if (!form.value.name.trim()) {
        newErrors.name = ["Name is required"];
    }

    if (!form.value.email.trim()) {
        newErrors.email = ["Email is required"];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
        newErrors.email = ["Email is invalid"];
    }

    if (!form.value.nip.trim()) {
        newErrors.nip = ["NIP is required"];
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;
    resetErrors();
    try {
        if (isEditing.value && props.teacher) {
            await teacherStore.updateTeacher(props.teacher.id, form.value);
        } else {
            await teacherStore.createTeacher(form.value);
        }
        resetForm();
        errors.value = {};
        emit("saved");
    } catch (error: any) {
        console.error("Save error:", error);

        if (error.response?.status === 422) {
            errors.value = error.response.data.errors;
            return;
        }
        throw error;
    } finally {
        loading.value = false;
    }
};
</script>
